import React, { useContext, useEffect, useState, useReducer } from 'react';
import { useTranslation } from 'react-i18next';
import AuthContext from '../../store/auth-context';
import Button, { ButtonType } from '../general-components/Button';
import LoadingRing from '../ui-components/LoadingRing';
import styles from './EditUserPage.module.css';
import { updateUserInfo, updateUserPassword } from '../../services/apiService';
import Modal from '../manage-user/Modal';
import UserInfoForm from './UserInfoForm';
import UserPasswordForm from './UserPasswordForm';
import {
    passwordValidate,
    repeatPasswordValidate,
    emailValidate,
    nameValidate,
} from '../../services/formValidationService';
import { getSubscriptionInfo } from '../../services/api/subscriptionService';
import PlanAndBilling from './PlanAndBilling';
import BackgroundContainer from '../ui-components/BackgroundContainer';
import FormInput from '../general-components/FormInput';
import PermissionSelector from './PermissionSelector';
import RIGHTS_KEYS from '../../constants/rights-keys';

const formReducer = (state: any, event: any): any => {
    return {
        ...state,
        [event.name]: event.value,
    };
};

const UserProfilePage = () => {
    const { t, i18n } = useTranslation();
    const context = useContext(AuthContext);

    const [userInfo, setUserInfo] = useState<any>([]);
    const [dataUserInfo, setDataUserInfo] = useReducer(formReducer, {});
    const [dataUserPassword, setDataUserPassword] = useReducer(formReducer, {});
    const [errorData, setErrorData] = useReducer(formReducer, {});
    const [isLoading, setIsLoading] = useState(true);
    const [isSent, setIsSent] = useState(false);
    const [fontSizeValue, setFontSizeValue] = useState('');
    const [modalResponse, setModalResponse] = useState({
        title: '',
        message: '',
    });

    const getData = async () => {
        const info = await context.getAllInfo();
        const settings = await context.getAllSettings();
        if (settings.fontSize) {
            setFontSizeValue(settings.fontSize);
        }
        console.log('info', info);
        getSubscriptionInfo();
        setUserInfo(info);
        setIsLoading(false);
    };

    useEffect(() => {
        getData();
    }, []);

    const handleInfoChange = (
        e: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => {
        const element = e.target as HTMLInputElement;
        if (element.value.length > 0) {
            errorData[element.name] = '';
        }
        setDataUserInfo({
            name: element.name,
            value: element.value,
        });
    };

    const handleInfoSubmit = async (e: any) => {
        e.preventDefault();
        if (!dataUserInfo.email) {
            delete dataUserInfo.email;
        } else {
            const emailValid = emailValidate(dataUserInfo.email);
            if (!emailValid.valid) {
                setErrorData({
                    name: 'email',
                    value: emailValid.message,
                });
                return;
            }
        }
        if (!dataUserInfo.name) {
            delete dataUserInfo.name;
        } else {
            const nameValid = nameValidate(dataUserInfo.name, true);
            if (!nameValid.valid) {
                setErrorData({
                    name: 'name',
                    value: nameValid.message,
                });
                return;
            }
        }
        const response = await updateUserInfo(dataUserInfo);
        setIsLoading(false);
        if (response) {
            setIsSent(true);
            setModalResponse({
                ...modalResponse,
                title: t('userPage.userProfilePage.modals.done.title'),
                message: t(
                    'userPage.userProfilePage.modals.done.messages.infoUpdated'
                ),
            });
        } else {
            const emailValid = emailValidate(dataUserInfo.email);
            if (!emailValid.valid) {
                setErrorData({
                    name: 'email',
                    value: emailValid.message,
                });
                return;
            }
        }
    };

    const setErrorOnField = (field: string) => {
        if (!dataUserPassword[field]) {
            setErrorData({
                name: field,
                value: t('userPage.userProfilePage.errors.fillField'),
            });
        }
    };

    const handlePasswordChange = (
        e: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => {
        const element = e.target as HTMLInputElement;
        if (element.value.length > 0) {
            errorData[element.name] = '';
        }
        setDataUserPassword({
            name: element.name,
            value: element.value,
        });
    };

    const handlePasswordSubmit = async (e: any) => {
        e.preventDefault();
        setErrorOnField('oldPassword');
        setErrorOnField('newPassword');
        setErrorOnField('repeatNewPassword');
        if (Object.values(dataUserPassword).every((value) => !value)) {
            setIsLoading(false);
            setIsSent(false);
            return;
        } else {
            const pw = passwordValidate(dataUserPassword.newPassword);
            const rpw = repeatPasswordValidate(
                dataUserPassword.newPassword,
                dataUserPassword.repeatNewPassword
            );
            if (!pw.valid) {
                setErrorData({
                    name: 'repeatNewPassword',
                    value: pw.message,
                });
            } else if (!rpw.valid) {
                setErrorData({
                    name: 'repeatNewPassword',
                    value: rpw.message,
                });
            } else {
                const response = await updateUserPassword(dataUserPassword);
                if (response.success === false) {
                    setErrorData({
                        name: 'oldPassword',
                        value: t(
                            'userPage.userProfilePage.errors.incorrectPassword'
                        ),
                    });
                    setIsSent(false);
                    setIsLoading(false);
                } else {
                    setIsLoading(false);
                    setIsSent(true);
                    setModalResponse({
                        ...modalResponse,
                        title: t('userPage.userProfilePage.modals.done.title'),
                        message: t(
                            'userPage.userProfilePage.modals.done.messages.passwordUpdated'
                        ),
                    });
                }
            }
        }
    };

    const handleCloseModal = () => {
        setIsSent(false);
    };

    return (
        <div className={styles.wrapper}>
            <div className={styles.userPage}>
                {isLoading && !isSent && (
                    <div className={styles.loading}>
                        <LoadingRing size="large" />
                    </div>
                )}
                {isSent && !isLoading && (
                    <Modal
                        info={{
                            title: modalResponse.title,
                            message: modalResponse.message,
                        }}
                        updateView={handleCloseModal}
                    >
                        <Button
                            type={ButtonType.secondary}
                            onClick={handleCloseModal}
                        >
                            {t('userPage.userProfilePage.buttons.close')}
                        </Button>
                    </Modal>
                )}
                {!isLoading && (
                    <div className={styles.info}>
                        <BackgroundContainer>
                            {context.orRights([
                                RIGHTS_KEYS.manageEmployees,
                            ]) && (
                                <PermissionSelector
                                    userId={userInfo.user._id}
                                    initialPermissions={
                                        userInfo.user.deptPermissions || []
                                    }
                                />
                            )}
                            <div className={styles.languageSelector}>
                                <h4>
                                    {t(
                                        'userPage.userProfilePage.languageSelector.title'
                                    )}
                                </h4>
                                <FormInput
                                    tag="select"
                                    name="language"
                                    onSelect={(e) => {
                                        const selectedLanguage = e.target.value;
                                        i18n.changeLanguage(selectedLanguage);
                                    }}
                                    defaultValue={i18n.language}
                                >
                                    <option value="en">
                                        {t(
                                            'userPage.userProfilePage.languageSelector.english'
                                        )}
                                    </option>
                                    <option value="da">
                                        {t(
                                            'userPage.userProfilePage.languageSelector.danish'
                                        )}
                                    </option>
                                    <option value="fr">
                                        {t(
                                            'userPage.userProfilePage.languageSelector.french'
                                        )}
                                    </option>
                                    <option value="fi">
                                        {t(
                                            'userPage.userProfilePage.languageSelector.finnish'
                                        )}
                                    </option>
                                    <option value="no">
                                        {t(
                                            'userPage.userProfilePage.languageSelector.norwegian'
                                        )}
                                    </option>
                                    <option value="pt-PT">
                                        {t(
                                            'userPage.userProfilePage.languageSelector.portuguesePT'
                                        )}
                                    </option>
                                    <option value="pt-BR">
                                        {t(
                                            'userPage.userProfilePage.languageSelector.portugueseBR'
                                        )}
                                    </option>
                                </FormInput>
                            </div>
                            <h3>{t('userPage.userProfilePage.title')}</h3>
                            <UserInfoForm
                                handleInfoChange={handleInfoChange}
                                handleInfoSubmit={handleInfoSubmit}
                                errorMsg={errorData}
                                defaultName={userInfo.user.name}
                                defaultEmail={userInfo.user.email}
                            />
                            <UserPasswordForm
                                handlePasswordChange={handlePasswordChange}
                                handlePasswordSubmit={handlePasswordSubmit}
                                errorMsg={errorData}
                            />
                        </BackgroundContainer>
                        {(context.getContext().actor.role === 'PremiumUser' ||
                            context.getContext().actor.role === 'LiteUser') && (
                            <PlanAndBilling />
                        )}

                        {/* <div>
                            <p>Text size - {fontSizeValue} </p>
                            <input
                                type="range"
                                min="10"
                                max="48"
                                value={fontSizeValue}
                                step="1"
                                onChange={(e) => {
                                    setFontSizeValue(e.target.value);
                                }}
                            />
                            <p style={{ fontSize: fontSizeValue + 'px' }}>
                                Lorem ipsum dolor, sit amet consectetur
                                adipisicing elit. Impedit corporis obcaecati aut
                                repellat nulla excepturi rem cumque neque at
                                unde suscipit ad dignissimos non, culpa error
                                nihil est nemo ut?
                            </p>
                            <Button
                                type={ButtonType.submit}
                                onClick={handleChangeFontSize}
                                className={styles.submitBtn}
                            >
                                Save text size
                            </Button>
                        </div> */}
                        {/* <Appearance /> */}
                    </div>
                )}
            </div>
        </div>
    );
};

export default UserProfilePage;
